import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import type {
	AcceptanceRevision,
	AcceptanceRevisionHistory,
	AcceptanceRevisionState,
	AutoModeStatus,
	AutoTaskProgressRow,
	DevelopmentStatus,
	ProjectSummary,
	TaskKind,
	TaskPriority,
	TaskRecord,
	TaskStatus,
	TicketRequest,
	TicketSpec,
} from "../types";
import { when } from "../components/RepeatField";
import { AcceptanceChecks, DeliveryView } from "../components/AutoTaskProgress";
import { DEVELOPMENT_STATUSES, DEVELOPMENT_STATUS_LABEL, KIND_LABEL, KINDS, PRIORITIES, PRIORITY_LABEL, STATUSES, STATUS_LABEL, statusLabel, statusPill, unpicked } from "./taskLabels";
import { ticketNext, type TicketAction, type TicketNext } from "./ticketNext";

function errorText(err: unknown, fallback: string): string {
	return err instanceof ApiError ? err.message : fallback;
}

type TaskPatch = Parameters<typeof api.updateTask>[1];

/**
 * A task's page: its title, its properties beside it, which save as they change, and its description, saved with
 * the title. A development ticket also shows where it stands in its lifecycle, what it waits on with the buttons
 * for it, its run and its spec; once an agent picked it up, its requirements read as text.
 */
export function TaskView(props: {
	task: TaskRecord;
	projects: ProjectSummary[];
	progress: AutoTaskProgressRow | null;
	/** Whether auto mode picks up; null where the server has none. */
	auto: boolean | null;
	onSaved: (task: TaskRecord) => void;
	onOpenDeploy: () => void;
	onOpenSession?: (id: string) => void;
	onAuto?: (status: AutoModeStatus) => void;
}) {
	const { task, progress } = props;
	const dev = task.kind === "development";
	const fixed = !unpicked(task);
	const [title, setTitle] = useState(task.title);
	const [description, setDescription] = useState(task.description);
	const [spec, setSpec] = useState<TicketSpec | null | undefined>(undefined);
	const [request, setRequest] = useState<TicketRequest | null>(null);
	const [revisions, setRevisions] = useState<AcceptanceRevisionHistory | null | undefined>(undefined);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const dirty = title !== task.title || description !== task.description;

	useEffect(() => {
		if (!dev) return;
		let live = true;
		api.ticketSpec(task.id).then(
			(answer) => live && setSpec(answer.spec),
			(err) => live && setError(errorText(err, "加载 spec 失败")),
		);
		return () => {
			live = false;
		};
	}, [dev, task.id, task.updatedAt]);

	useEffect(() => {
		if (!dev) return;
		let live = true;
		api.ticketRequest(task.id).then(
			(answer) => live && setRequest(answer.request),
			() => live && setRequest(null),
		);
		return () => {
			live = false;
		};
	}, [dev, task.id]);

	useEffect(() => {
		if (!dev) return;
		let live = true;
		api.acceptanceRevisions(task.id).then(
			(answer) => live && setRevisions(Array.isArray(answer?.revisions) ? answer : null),
			() => live && setRevisions(null),
		);
		return () => {
			live = false;
		};
	}, [dev, task.id, task.updatedAt]);

	async function run(action: () => Promise<void>, fallback: string) {
		setBusy(true);
		setError(null);
		try {
			await action();
		} catch (err) {
			setError(errorText(err, fallback));
		} finally {
			setBusy(false);
		}
	}

	const save = (patch: TaskPatch) => run(async () => props.onSaved((await api.updateTask(task.id, patch)).task), "保存失败");

	function submit() {
		if (!title.trim()) return setError("标题不能为空");
		void run(async () => {
			const saved = (await api.updateTask(task.id, { title: title.trim(), description })).task;
			setTitle(saved.title);
			setDescription(saved.description);
			props.onSaved(saved);
		}, "保存失败");
	}

	function revert() {
		setTitle(task.title);
		setDescription(task.description);
		setError(null);
	}

	const discuss = () =>
		run(async () => {
			const { session } = await api.ticketDiscussion(task.id);
			props.onOpenSession?.(session.id);
		}, "无法打开 dev 讨论");

	const act: Record<Exclude<TicketAction["kind"], "loop">, () => void> = {
		discuss: () => void discuss(),
		confirm: () =>
			spec &&
			void run(async () => {
				const answer = await api.confirmTicketSpec(task.id, spec.version);
				setSpec(answer.spec);
				props.onSaved(answer.task);
			}, "确认失败"),
		deploy: props.onOpenDeploy,
		stop: () => progress?.loopId && void run(async () => void (await api.abortLoop(progress.loopId!)), "停止失败"),
		resume: () => void run(async () => props.onAuto?.(await api.resumeAutoTask(task.id)), "恢复执行失败"),
	};

	const next = dev ? ticketNext({ task: { status: task.status as DevelopmentStatus, archived: task.archived }, spec, progress, auto: props.auto }) : null;

	return (
		<article className="task-doc">
			<header className="task-doc-head">
				<div className="task-kicker">
					<span className="pill task-kind">{KIND_LABEL[task.kind]}</span>
					{dev && <span className={statusPill(task)}>{statusLabel(task)}</span>}
					{task.archived && <span className="pill">已归档</span>}
				</div>
				{fixed ? (
					<h2>{task.title}</h2>
				) : (
					<label className="task-title">
						<span className="visually-hidden">标题</span>
						<input value={title} onChange={(e) => setTitle(e.target.value)} />
					</label>
				)}
			</header>
			<aside className="task-props" aria-label="属性">
				<TaskProps task={task} projects={props.projects} busy={busy} onSave={(patch) => void save(patch)} />
				{dev && <TaskDependencies task={task} onSaved={props.onSaved} />}
				<p className="task-stamp">
					创建于 {when(task.createdAt)} · 更新于 {when(task.updatedAt)}
				</p>
				{request && (
					<p className="task-stamp">
						来源 {request.profile}
						{props.onOpenSession && (
							<>
								{" · "}
								<button className="link" onClick={() => props.onOpenSession?.(request.sessionId)}>
									沟通记录
								</button>
							</>
						)}
					</p>
				)}
				{dev && (
					<div className="row task-prop-actions">
						<button onClick={() => void discuss()} disabled={busy}>
							dev 讨论
						</button>
						<button className="ghost" onClick={() => void save({ archived: !task.archived })} disabled={busy}>
							{task.archived ? "取消归档" : "归档"}
						</button>
					</div>
				)}
			</aside>
			<div className="task-doc-main">
				{error && <p className="banner error">{error}</p>}
				{dev && <TicketSteps task={task} />}
				{next && <TicketNextCard next={next} loopId={progress?.loopId ?? null} busy={busy} act={act} />}
				{dev && progress?.execution && <TicketRun progress={progress} />}
				{dev && <TicketSpecSection spec={spec} />}
				{dev && (fixed || (revisions?.revisions.length ?? 0) > 0) && <AcceptanceRevisionsSection history={revisions} />}
				{fixed ? (
					<section className="task-sec">
						<h4>描述</h4>
						{task.description ? <p className="task-desc">{task.description}</p> : <p className="hint">暂无描述</p>}
					</section>
				) : (
					<>
						<label className="task-sec">
							<span className="task-sec-title">描述</span>
							<textarea value={description} rows={6} onChange={(e) => setDescription(e.target.value)} />
						</label>
						<div className="row">
							<button onClick={submit} disabled={busy || !dirty}>
								保存
							</button>
							<button className="ghost" onClick={revert} disabled={!dirty}>
								取消
							</button>
						</div>
					</>
				)}
			</div>
		</article>
	);
}

/** The properties beside a task, each saved as it changes; a ticket's state is its lifecycle's, and its kind is fixed once picked up. */
function TaskProps({ task, projects, busy, onSave }: { task: TaskRecord; projects: ProjectSummary[]; busy: boolean; onSave: (patch: TaskPatch) => void }) {
	// A link to an archived or removed project stays selectable under its kept name.
	const staleLink = task.projectId && !projects.some((p) => p.id === task.projectId) ? task : null;
	return (
		<>
			{task.kind === "development" ? (
				<div className="task-prop">
					<span className="task-prop-label">状态</span>
					<span className="task-prop-value">
						<span className={statusPill(task)}>{statusLabel(task)}</span>
					</span>
				</div>
			) : (
				<label className="task-prop">
					<span className="task-prop-label">状态</span>
					<select value={task.status} disabled={busy} onChange={(e) => onSave({ status: e.target.value as TaskStatus })}>
						{STATUSES.map((s) => (
							<option key={s} value={s}>
								{STATUS_LABEL[s]}
							</option>
						))}
					</select>
				</label>
			)}
			{unpicked(task) ? (
				<label className="task-prop">
					<span className="task-prop-label">类型</span>
					<select value={task.kind} disabled={busy} onChange={(e) => onSave({ kind: e.target.value as TaskKind })}>
						{KINDS.map((k) => (
							<option key={k} value={k}>
								{KIND_LABEL[k]}
							</option>
						))}
					</select>
				</label>
			) : (
				<div className="task-prop">
					<span className="task-prop-label">类型</span>
					<span className="task-prop-value">{KIND_LABEL[task.kind]}</span>
				</div>
			)}
			<label className="task-prop">
				<span className="task-prop-label">优先级</span>
				<select value={task.priority} disabled={busy} onChange={(e) => onSave({ priority: e.target.value as TaskPriority })}>
					{PRIORITIES.map((p) => (
						<option key={p} value={p}>
							{PRIORITY_LABEL[p]}
						</option>
					))}
				</select>
			</label>
			<label className="task-prop">
				<span className="task-prop-label">截止</span>
				<input type="date" value={task.dueDate ?? ""} disabled={busy} onChange={(e) => onSave({ dueDate: e.target.value || null })} />
			</label>
			<label className="task-prop">
				<span className="task-prop-label">项目</span>
				<select value={task.projectId ?? ""} disabled={busy} onChange={(e) => onSave({ projectId: e.target.value || null })}>
					<option value="">不关联项目</option>
					{staleLink && (
						<option value={staleLink.projectId!}>
							{staleLink.projectTitle ?? staleLink.projectId}（已归档）
						</option>
					)}
					{projects.map((p) => (
						<option key={p.id} value={p.id}>
							{p.title}
						</option>
					))}
				</select>
			</label>
		</>
	);
}

/** A new task, written before it exists: the title, the kind, the description and the same properties. */
export function NewTask(props: { projects: ProjectSummary[]; titled: boolean; onCancel: () => void; onSaved: (task: TaskRecord) => void }) {
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [kind, setKind] = useState<TaskKind>("ordinary");
	const [status, setStatus] = useState<TaskStatus>("todo");
	const [priority, setPriority] = useState<TaskPriority>("medium");
	const [dueDate, setDueDate] = useState("");
	const [projectId, setProjectId] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function submit() {
		if (!title.trim()) return setError("标题不能为空");
		setBusy(true);
		setError(null);
		try {
			const fields = { title: title.trim(), description, priority, dueDate: dueDate || null, kind, projectId: projectId || null };
			props.onSaved((await api.createTask({ ...fields, ...(kind === "ordinary" ? { status } : {}) })).task);
		} catch (err) {
			setError(errorText(err, "保存失败"));
		} finally {
			setBusy(false);
		}
	}

	return (
		<article className="task-doc">
			<header className="task-doc-head">
				{props.titled && <h2>新建任务</h2>}
				<label className="task-sec">
					<span className="task-sec-title">标题</span>
					<input value={title} onChange={(e) => setTitle(e.target.value)} />
				</label>
			</header>
			<aside className="task-props" aria-label="属性">
				{kind === "ordinary" && (
					<label className="task-prop">
						<span className="task-prop-label">状态</span>
						<select value={status} onChange={(e) => setStatus(e.target.value as TaskStatus)}>
							{STATUSES.map((s) => (
								<option key={s} value={s}>
									{STATUS_LABEL[s]}
								</option>
							))}
						</select>
					</label>
				)}
				<label className="task-prop">
					<span className="task-prop-label">优先级</span>
					<select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
						{PRIORITIES.map((p) => (
							<option key={p} value={p}>
								{PRIORITY_LABEL[p]}
							</option>
						))}
					</select>
				</label>
				<label className="task-prop">
					<span className="task-prop-label">截止</span>
					<input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
				</label>
				<label className="task-prop">
					<span className="task-prop-label">项目</span>
					<select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
						<option value="">不关联项目</option>
						{props.projects.map((p) => (
							<option key={p.id} value={p.id}>
								{p.title}
							</option>
						))}
					</select>
				</label>
			</aside>
			<div className="task-doc-main">
				<div className="task-sec" role="group" aria-label="类型">
					<span className="task-sec-title">类型</span>
					<div className="task-seg">
						{KINDS.map((k) => (
							<button key={k} aria-pressed={kind === k} className={kind === k ? "on" : ""} onClick={() => setKind(k)}>
								{KIND_LABEL[k]}
							</button>
						))}
					</div>
				</div>
				<label className="task-sec">
					<span className="task-sec-title">描述</span>
					<textarea value={description} rows={6} onChange={(e) => setDescription(e.target.value)} />
				</label>
				{error && <p className="banner error">{error}</p>}
				<div className="row">
					<button onClick={() => void submit()} disabled={busy}>
						创建
					</button>
					<button className="ghost" onClick={props.onCancel}>
						取消
					</button>
				</div>
			</div>
		</article>
	);
}

const LIFECYCLE: DevelopmentStatus[] = ["undefined", "ready", "in_progress", "ready_to_deploy", "completed"];

/** Where a development ticket stands in its lifecycle, from an undefined idea to a deployed change. */
function TicketSteps({ task }: { task: TaskRecord }) {
	const at = LIFECYCLE.indexOf(task.status as DevelopmentStatus);
	return (
		<ol className="ticket-steps" aria-label="开发进度">
			{LIFECYCLE.map((status, i) => (
				<li key={status} data-step className={i < at ? "done" : i === at ? "now" : ""} aria-current={i === at ? "step" : undefined}>
					<i />
					{DEVELOPMENT_STATUS_LABEL[status]}
				</li>
			))}
		</ol>
	);
}

/** What the ticket waits on, with the buttons for it. */
function TicketNextCard({ next, loopId, busy, act }: { next: TicketNext; loopId: string | null; busy: boolean; act: Record<Exclude<TicketAction["kind"], "loop">, () => void> }) {
	return (
		<section className={`loop-now tone-${next.tone}`} aria-label={next.label}>
			<span className="loop-now-label">{next.label}</span>
			<p className="loop-now-text">{next.text}</p>
			{next.detail && <p className="hint task-next-detail">{next.detail}</p>}
			{next.actions.length > 0 && (
				<div className="row">
					{next.actions.map((a, i) =>
						a.kind === "loop" ? (
							<a key={a.kind} className="task-button-link" href={`/?loop=${encodeURIComponent(loopId ?? "")}`}>
								{a.label}
							</a>
						) : (
							<button key={a.kind} className={i === 0 ? "" : "ghost"} onClick={act[a.kind]} disabled={busy}>
								{a.label}
							</button>
						),
					)}
				</div>
			)}
		</section>
	);
}

/** A picked ticket's latest launch: its change and branch, its checks and where its change stands in the push lane. */
function TicketRun({ progress }: { progress: AutoTaskProgressRow }) {
	const execution = progress.execution!;
	return (
		<section className="task-sec" aria-label="执行">
			<h4>执行</h4>
			<dl className="loop-info">
				{progress.change && (
					<div>
						<dt>改动</dt>
						<dd>
							<code>{progress.change}</code>
							{execution.kind === "one-shot" && (
								<>
									{" "}
									<span className="pill">one-shot</span>
								</>
							)}
						</dd>
					</div>
				)}
				<div>
					<dt>分支</dt>
					<dd>
						<code>{execution.branch}</code>
					</dd>
				</div>
				{progress.acceptance.length > 0 && (
					<div>
						<dt>验收检查</dt>
						<dd>
							<AcceptanceChecks results={progress.acceptance} />
						</dd>
					</div>
				)}
				{progress.delivery && (
					<div>
						<dt>推送</dt>
						<dd>
							<DeliveryView delivery={progress.delivery} brief />
						</dd>
					</div>
				)}
			</dl>
		</section>
	);
}

/** The exact spec version dev prepared, open while it waits for the person's confirmation. */
function TicketSpecSection({ spec }: { spec: TicketSpec | null | undefined }) {
	return (
		<section className="task-sec" aria-label="spec">
			<h4>Spec</h4>
			{spec === undefined ? (
				<p className="hint">加载中…</p>
			) : spec === null ? (
				<p className="hint">暂无 spec</p>
			) : (
				<>
					<p className="hint">
						版本 <code>{spec.version}</code> · 改动 <code>{spec.change}</code> · {spec.stale ? "已过期" : spec.confirmed ? "已确认" : "待确认"}
					</p>
					<details className="task-fold" open={!spec.confirmed && !spec.stale}>
						<summary>查看 spec</summary>
						<pre className="loop-output ticket-spec">{spec.content}</pre>
					</details>
				</>
			)}
		</section>
	);
}

const REVISION_STATE: Record<AcceptanceRevisionState, string> = {
	prepared: "待检查",
	attested: "dev 已证明 · 待批准",
	approved: "已批准",
	superseded: "已被取代",
	stale: "已过期",
};

const short = (sha: string) => sha.slice(0, 8);

/**
 * A picked ticket's acceptance revisions, newest first: each repair of its defective check with its reason, the run it
 * repairs, its difference, dev's checks and attestation and the owner's approval. Read-only: dev prepares a revision and
 * the owner approves it in the ticket's dev discussion.
 */
function AcceptanceRevisionsSection({ history }: { history: AcceptanceRevisionHistory | null | undefined }) {
	return (
		<section className="task-sec" aria-label="验收修订">
			<h4>验收修订</h4>
			{history === undefined ? (
				<p className="hint">加载中…</p>
			) : history === null ? (
				<p className="hint">加载验收修订失败</p>
			) : history.revisions.length === 0 ? (
				<p className="hint">暂无验收修订</p>
			) : (
				<>
					<p className="hint">
						{history.activeVersion ? (
							<>
								生效版本 <code>{history.activeVersion}</code>
							</>
						) : (
							"暂无已批准修订"
						)}
					</p>
					<ol className="task-revisions">
						{[...history.revisions].reverse().map((revision) => (
							<AcceptanceRevisionItem key={revision.version} revision={revision} active={revision.version === history.activeVersion} />
						))}
					</ol>
				</>
			)}
		</section>
	);
}

function AcceptanceRevisionItem({ revision: r, active }: { revision: AcceptanceRevision; active: boolean }) {
	const review = r.reviews.at(-1);
	const texts = r.after.files.filter((f) => f.encoding === "utf8");
	return (
		<li className="task-revision">
			<p>
				<code>{r.version}</code> <span className="pill">{REVISION_STATE[r.state]}</span>
				{active && <span className="pill">生效</span>}
			</p>
			<p className="task-desc">{r.reason}</p>
			<dl className="loop-info">
				<div>
					<dt>替换</dt>
					<dd>{r.parentVersion ? <code>{r.parentVersion}</code> : "确认 spec 时的检查"}</dd>
				</div>
				<div>
					<dt>修复的运行</dt>
					<dd>
						<code>{r.loopId}</code> · <code>{r.branch}</code> @ <code>{short(r.worktreeSha)}</code>
					</dd>
				</div>
				{r.stateReason && (
					<div>
						<dt>说明</dt>
						<dd>{r.stateReason}</dd>
					</div>
				)}
				<div>
					<dt>检查</dt>
					<dd>
						{review
							? `main ${short(review.main.sha)}：${review.main.outcome}（exit ${review.main.exitCode ?? "无"}）· 分支 ${short(review.worktree.sha)}：${review.worktree.outcome}（exit ${review.worktree.exitCode ?? "无"}）`
							: "未检查"}
					</dd>
				</div>
				<div>
					<dt>dev 证明</dt>
					<dd>{r.attestation ? `${when(r.attestation.attestedAt)} · ${r.attestation.reason}` : "未证明"}</dd>
				</div>
				<div>
					<dt>批准</dt>
					<dd>{r.approval ? `${r.approval.owner} 于 ${when(r.approval.confirmedAt)} 批准` : "未批准"}</dd>
				</div>
			</dl>
			<details className="task-fold" open={active || r.state === "attested"}>
				<summary>差异</summary>
				<pre className="loop-output">{r.diff}</pre>
			</details>
			<details className="task-fold">
				<summary>新检查</summary>
				{texts.map((f) => (
					<pre key={f.path} className="loop-output">
						{`# ${f.path}\n${f.content}`}
					</pre>
				))}
			</details>
		</li>
	);
}

const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * A development ticket's explicit prerequisites, by name, chosen from the owner's other development tickets in any
 * state while no agent picked it up. Saving sends only the ids and certifies nothing: dev verifies readiness again.
 */
function TaskDependencies(props: { task: TaskRecord; onSaved: (task: TaskRecord) => void }) {
	const { task } = props;
	const [saved, setSaved] = useState<string[]>(task.dependsOn ?? []);
	const [chosen, setChosen] = useState<string[]>(saved);
	const [picking, setPicking] = useState(false);
	const [owned, setOwned] = useState<TaskRecord[] | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const editable = unpicked(task);

	useEffect(() => {
		api.tasks({ kind: "development", status: DEVELOPMENT_STATUSES }).then(
			(answer) => setOwned(answer.tasks),
			(err) => setError(errorText(err, "加载可选的任务失败")),
		);
	}, [task.id]);

	async function save() {
		setBusy(true);
		setError(null);
		try {
			const answer = (await api.updateTask(task.id, { dependsOn: chosen })).task;
			const next = answer.dependsOn ?? chosen;
			setSaved(next);
			setChosen(next);
			setPicking(false);
			props.onSaved(answer);
		} catch (err) {
			setError(errorText(err, "保存依赖失败"));
		} finally {
			setBusy(false);
		}
	}

	function cancel() {
		setChosen(saved);
		setPicking(false);
		setError(null);
	}

	const offered = owned?.filter((t) => t.id !== task.id) ?? [];
	// A saved dependency no longer offered stays chosen until removed.
	const stale = saved.filter((id) => !offered.some((t) => t.id === id));
	const name = (id: string) => {
		const title = owned?.find((t) => t.id === id)?.title ?? id;
		return owned && stale.includes(id) ? `${title}（未找到）` : title;
	};

	return (
		<div className="task-prop task-deps" role="group" aria-label="依赖">
			<span className="task-prop-label">依赖</span>
			<span className="task-prop-value task-chips">
				{saved.length === 0 ? (
					<span className="hint">无</span>
				) : (
					saved.map((id) => (
						<span key={id} className="task-chip">
							{name(id)}
						</span>
					))
				)}
				{editable && !picking && (
					<button className="ghost task-chip-add" onClick={() => setPicking(true)}>
						选择…
					</button>
				)}
			</span>
			{picking && (
				<div className="task-deps-pick">
					{owned && (
						<label>
							<span className="visually-hidden">依赖</span>
							<select multiple value={chosen} onChange={(e) => setChosen(Array.from(e.target.selectedOptions, (o) => o.value))}>
								{offered.map((t) => (
									<option key={t.id} value={t.id}>
										{t.title}
									</option>
								))}
								{stale.map((id) => (
									<option key={id} value={id}>
										{name(id)}
									</option>
								))}
							</select>
						</label>
					)}
					{owned && offered.length === 0 && <p className="hint">暂无可选开发任务</p>}
					<div className="row">
						<button onClick={() => void save()} disabled={busy || !owned || sameIds(chosen, saved)}>
							保存依赖
						</button>
						<button className="ghost" onClick={cancel}>
							取消
						</button>
					</div>
				</div>
			)}
			{error && <p className="banner error">{error}</p>}
		</div>
	);
}
